/**
 * A10 — Annexes & Notes: FE form ↔ BE situation_annexes_notes column mapping.
 * Writable columns only (excludes id, annexe_ref, created_at, updated_at).
 */

export const ANNEXES_WRITABLE_FIELDS = [
  'exercice_fiscal',
  'periode_debut',
  'periode_fin',
  'type_annexe',
  'categorie_annexe',
  'titre_annexe',
  'description_annexe',
  'numero_annexe',
  'ordre_affichage',
  'contenu_texte',
  'contenu_html',
  'contenu_json',
  'tableau_data',
  'tableau_colonnes',
  'graphique_type',
  'graphique_data',
  'graphique_config',
  'reference_bilan',
  'reference_resultat',
  'reference_comptes',
  'montants_exercice_courant',
  'montants_exercice_precedent',
  'variations_montants',
  'variations_pourcentage',
  'methodes_evaluation',
  'principes_comptables',
  'evenements_significatifs',
  'faits_marquants',
  'provisions_constituees',
  'provisions_reprises',
  'engagements_hors_bilan',
  'passifs_eventuels',
  'parties_liees_transactions',
  'parties_liees_soldes',
  'effectifs_personnel',
  'remuneration_dirigeants',
  'honoraires_auditeur',
  'honoraires_conseil',
  'litiges_en_cours',
  'procedures_judiciaires',
  'changements_methodes',
  'changements_estimations',
  'evenements_posterieurs',
  'evenements_ajustement',
  'hypotheses_cles',
  'sources_incertitude',
  'informations_sectorielles',
  'informations_geographiques',
  'fichiers_joints',
  'fichiers_nombre',
  'fichiers_taille_totale',
  'statut_annexe',
  'validation_daf',
  'validation_auditeur',
  'validation_date',
  'publication_requise',
  'publication_date',
  'confidentialite_niveau',
  'acces_restreint',
  'version_numero',
  'version_precedente_ref',
  'commentaires_redaction',
  'commentaires_revision',
  'conformite_pcn',
  'conformite_ifrs',
  'conformite_reglementaire',
  'notes',
  'metadata',
  'created_by',
];

const DATE_FIELDS = new Set([
  'periode_debut',
  'periode_fin',
  'validation_date',
  'publication_date',
]);

const DECIMAL_FIELDS = new Set([
  'remuneration_dirigeants',
  'honoraires_auditeur',
  'honoraires_conseil',
  'fichiers_taille_totale',
]);

const INTEGER_FIELDS = new Set([
  'numero_annexe',
  'ordre_affichage',
  'effectifs_personnel',
  'fichiers_nombre',
  'version_numero',
  'evenements_ajustement',
  'validation_daf',
  'validation_auditeur',
  'publication_requise',
  'acces_restreint',
  'conformite_pcn',
  'conformite_ifrs',
  'conformite_reglementaire',
]);

const INTEGER_DEFAULTS = Object.freeze({
  numero_annexe: 0,
  ordre_affichage: 0,
  effectifs_personnel: 0,
  fichiers_nombre: 0,
  version_numero: 1,
  evenements_ajustement: 0,
  validation_daf: 0,
  validation_auditeur: 0,
  publication_requise: 0,
  acces_restreint: 0,
  conformite_pcn: 1,
  conformite_ifrs: 0,
  conformite_reglementaire: 1,
});

const BOOLEAN_FORM_FIELDS = new Set([
  'validation_daf',
  'validation_auditeur',
  'publication_requise',
  'acces_restreint',
  'evenements_ajustement',
  'conformite_pcn',
  'conformite_ifrs',
  'conformite_reglementaire',
]);

export const EMPTY_ANNEXES_FORM = Object.freeze({
  ...ANNEXES_WRITABLE_FIELDS.reduce((acc, field) => {
    if (BOOLEAN_FORM_FIELDS.has(field)) {
      acc[field] = INTEGER_DEFAULTS[field] === 1;
    } else if (INTEGER_FIELDS.has(field)) {
      acc[field] = INTEGER_DEFAULTS[field] ?? '';
    } else {
      acc[field] = '';
    }
    return acc;
  }, {}),
  type_annexe: 'Note Explicative',
  statut_annexe: 'Brouillon',
});

const normalizeDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  return String(value);
};

const toFormScalar = (field, value) => {
  if (value == null) {
    if (BOOLEAN_FORM_FIELDS.has(field)) return INTEGER_DEFAULTS[field] === 1;
    if (INTEGER_FIELDS.has(field)) return INTEGER_DEFAULTS[field] ?? '';
    return '';
  }
  if (DATE_FIELDS.has(field)) return normalizeDate(value);
  if (BOOLEAN_FORM_FIELDS.has(field)) return value === 1 || value === true;
  return value;
};

/** Map API row → form state (all writable columns). */
export const recordToFormData = (record = {}) => {
  const form = { ...EMPTY_ANNEXES_FORM };
  ANNEXES_WRITABLE_FIELDS.forEach((field) => {
    if (record[field] !== undefined && record[field] !== null) {
      form[field] = toFormScalar(field, record[field]);
    }
  });
  return form;
};

/** Map form state → API payload (typed for situation_annexes_notes). */
export const buildAnnexesPayload = (formData = {}) => {
  const payload = {};

  ANNEXES_WRITABLE_FIELDS.forEach((field) => {
    const raw = formData[field];

    if (DECIMAL_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? 0 : parseFloat(raw) || 0;
      return;
    }

    if (INTEGER_FIELDS.has(field)) {
      if (raw === '' || raw == null) {
        payload[field] = INTEGER_DEFAULTS[field] ?? 0;
        return;
      }
      payload[field] = raw === true || raw === 1 || raw === '1' ? 1 : 0;
      return;
    }

    if (DATE_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? null : normalizeDate(raw);
      return;
    }

    payload[field] = raw === '' || raw == null ? null : raw;
  });

  return payload;
};
